import { Duration } from 'luxon'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { offlineSyncStore } from '../context/GraphQLProvider/offlineSync'
import { BUILD_VERSION, WEB_UPDATER_ENABLED } from '../data/environment'
import { getIsOnline } from '../helpers/networkStatus'

const AUTOMATIC_UPDATE_CHECK_DURATION = Duration.fromObject({ minutes: 15 })

/** True when the deployed build (`/version.json`, written by vite.config) differs from this one. */
const isNewerBuildDeployed = async (): Promise<boolean> => {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' })
    if (!response.ok) return false
    const { version } = (await response.json()) as { version?: string }
    return Boolean(version) && version !== BUILD_VERSION
  } catch {
    // Offline or a malformed response — check again on the next tick.
    return false
  }
}

/**
 * Web counterpart of `CapacitorUpdateOnLocationChange` (native updates via
 * Capgo). Checks for a new deploy on load, on focus and every 15 minutes, then
 * reloads on the next navigation so a tab left open picks up the new build
 * between screens rather than mid-task.
 *
 * Never reloads while offline (there is no service worker, so a reload
 * without a connection would land on the browser's offline page) nor while a
 * mutation is in flight: a replayed offline write is already gone from the
 * queue, so aborting it would lose it. It waits for a later navigation.
 */
export const WebUpdateOnNavigation = () => {
  const { pathname } = useLocation()
  const updateReady = useRef(false)

  useEffect(() => {
    if (!WEB_UPDATER_ENABLED) return
    const checkForUpdate = async () => {
      if (await isNewerBuildDeployed()) updateReady.current = true
    }
    checkForUpdate()
    const interval = setInterval(
      checkForUpdate,
      AUTOMATIC_UPDATE_CHECK_DURATION.toMillis(),
    )
    window.addEventListener('focus', checkForUpdate)
    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', checkForUpdate)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on navigation; pathname is the trigger, not an input
  useEffect(() => {
    const canReloadSafely =
      getIsOnline() && offlineSyncStore.getInFlightMutations() === 0
    const shouldReload = updateReady.current && canReloadSafely
    if (shouldReload) window.location.reload()
  }, [pathname])

  return null
}
